import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import { orderService } from "../services/apiService";
import Toast from "../components/Toast";
import { useBill } from "../hooks/useBill";
import { useLoader } from "../utils/LoaderContext";
import { useProfile } from "../utils/UseProfile";
import ReturnModal from "../components/ReturnModal";
import ReturnHistoryModal from "../components/ReturnHistoryModal";

const SortIcon = ({ col, sortConfig }) => {
  if (!sortConfig || sortConfig.key !== col) return <i className="fas fa-sort text-gray-300 ml-1 text-xs" />;
  return sortConfig.dir === "asc" ? <i className="fas fa-sort-up text-indigo-500 ml-1 text-xs" /> : <i className="fas fa-sort-down text-indigo-500 ml-1 text-xs" />;
};

const Th = ({ col, label, className = "text-left", sortConfig, onSort }) => (
  <th className={`py-2 px-3 ${className} cursor-pointer select-none hover:bg-blue-100`} onClick={() => onSort(col)}>
    {label}<SortIcon col={col} sortConfig={sortConfig} />
  </th>
);

const OrderSearch = () => {
  const SS = "order_search";
  const ss = () => {
    try {
      return JSON.parse(sessionStorage.getItem(SS)) || {};
    } catch {
      return {};
    }
  };
  const saveSession = (patch) => {
    try {
      sessionStorage.setItem(SS, JSON.stringify({ ...ss(), ...patch }));
    } catch {}
  };
  const { showLoader, hideLoader } = useLoader();
  const PAGE_SIZE = 15;

  const [searchForm, setSearchForm] = useState(
    () =>
      ss().searchForm || {
        orderNo: "",
        customerMobile: "",
        fromDate: "",
        toDate: "",
        paymentType: "",
      },
  );
  const [orders, setOrders] = useState(() => ss().orders || []);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [hasSearched, setHasSearched] = useState(
    () => ss().hasSearched || false,
  );
  const [page, setPage] = useState(() => ss().page || 0);
  const [totalPages, setTotalPages] = useState(() => ss().totalPages || 0);
  const [totalElements, setTotalElements] = useState(
    () => ss().totalElements || 0,
  );
  const { downloadBillPDF, previewBillPDF, printBill, loading: billLoading } = useBill();
  const { profile } = useProfile();
  const role = profile?.roleResponse?.role;
  const isAdmin = Array.isArray(role)
    ? role.some((r) => r.toUpperCase().includes("ADMIN"))
    : typeof role === "string" && role.toUpperCase().includes("ADMIN");

  const [returnModal, setReturnModal] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [printTarget, setPrintTarget] = useState(null);
  const [actionOpen, setActionOpen] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, dir: "asc" });
  const handleSort = (key) => setSortConfig((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));

  const sortedOrders = [...orders].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = a[sortConfig.key] ?? "";
    const bVal = b[sortConfig.key] ?? "";
    const cmp = typeof aVal === "number" && typeof bVal === "number" ? aVal - bVal : String(aVal).localeCompare(String(bVal));
    return sortConfig.dir === "asc" ? cmp : -cmp;
  });

  useEffect(() => {
    const onUnload = () => sessionStorage.setItem(SS + "_refresh", "1");
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      if (!sessionStorage.getItem(SS + "_refresh"))
        sessionStorage.removeItem(SS);
      sessionStorage.removeItem(SS + "_refresh");
    };
  }, []);

  const updateForm = (field, value) => {
    setSearchForm((prev) => {
      const u = { ...prev, [field]: value };
      saveSession({ searchForm: u });
      return u;
    });
  };

  const fetchOrders = async (pageNo = 0) => {
    showLoader();
    setLoading(true);
    setHasSearched(true);
    saveSession({ hasSearched: true });

    try {
      const data = await orderService.searchOrders({
        orderNo: searchForm.orderNo.trim() || null,
        customerMobile: searchForm.customerMobile.trim() || null,
        fromDate: searchForm.fromDate || null,
        toDate: searchForm.toDate || null,
        paymentType: searchForm.paymentType || null,
        page: pageNo,
        size: PAGE_SIZE,
      });
      const list = data.content ?? [];
      const tp = data.totalPages ?? 0;
      const te = data.totalElements ?? 0;
      setOrders(list);
      setPage(pageNo);
      setTotalPages(tp);
      setTotalElements(te);
      saveSession({
        orders: list,
        page: pageNo,
        totalPages: tp,
        totalElements: te,
      });
      if (!list.length) setToast({ type: "error", message: "No orders found" });
      else setToast({ type: "success", message: `Found ${te} order(s)` });
    } catch (err) {
      setToast({
        type: "error",
        message: err.response?.data?.message || "Failed to search orders",
      });
      setOrders([]);
    } finally {
      setLoading(false);
      hideLoader(); // ✅ ALWAYS STOP LOADER
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const hasAny = Object.values(searchForm).some((v) => v.trim());
    if (!hasAny) {
      setToast({
        type: "error",
        message: "Please enter at least one search criteria",
      });
      return;
    }
    fetchOrders(0);
  };

  // ── AI "open this page" handoff ─────────────────────────────────────────
  // If Jash asked to open Invoice Search with prefilled filters, apply them to
  // searchForm on mount and (optionally) run the existing search once. This
  // reuses the existing fetchOrders — search behavior is unchanged.
  const [navShouldSearch, setNavShouldSearch] = useState(false);
  useEffect(() => {
    let nav = null;
    try {
      const raw = sessionStorage.getItem("jashNav");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.page === "invoice_search") nav = parsed;
      }
    } catch { nav = null; }
    if (!nav) return;
    try { sessionStorage.removeItem("jashNav"); } catch { /* noop */ }
    const p = nav.prefill || {};
    setSearchForm((prev) => ({
      ...prev,
      orderNo: p.orderNo ?? prev.orderNo,
      customerMobile: p.customerMobile ?? prev.customerMobile,
      fromDate: p.fromDate ?? prev.fromDate,
      toDate: p.toDate ?? prev.toDate,
      paymentType: p.paymentType ?? prev.paymentType,
    }));
    if (nav.autoSearch !== false) setNavShouldSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!navShouldSearch) return;
    setNavShouldSearch(false);
    fetchOrders(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navShouldSearch, searchForm]);

  const handlePageChange = (pageNo) => {
    fetchOrders(pageNo);
  };

  const handleReset = () => {
    setSearchForm({
      orderNo: "",
      customerMobile: "",
      fromDate: "",
      toDate: "",
      transactionType: "",
    });
    setOrders([]);
    setToast(null);
    setHasSearched(false);
    setPage(0);
    setTotalPages(0);
    setTotalElements(0);
    sessionStorage.removeItem(SS);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4">
      <Header showSearch={false} showCart={false} />
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

      {/* Search filters */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-4 flex-shrink-0">
        <h2 className="text-left text-sm font-bold text-blue-800 px-4 py-2 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          🔍 Search Orders
        </h2>
        <div className="p-2">
          <form onSubmit={handleSearch}>
            <div className="flex flex-wrap gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Order Number
                </label>
                <input
                  type="text"
                  value={searchForm.orderNo}
                  placeholder="e.g. ORD-001"
                  onChange={(e) => updateForm("orderNo", e.target.value)}
                  className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Customer Mobile
                </label>
                <input
                  type="tel"
                  value={searchForm.customerMobile}
                  placeholder="e.g. 9876543210"
                  maxLength="10"
                  onChange={(e) =>
                    updateForm(
                      "customerMobile",
                      e.target.value.replace(/\D/g, "").slice(0, 10),
                    )
                  }
                  className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Transaction Type
                </label>
                <select
                  value={searchForm.paymentType}
                  onChange={(e) => updateForm("paymentType", e.target.value)}
                  className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
                >
                  <option value="">All</option>
                  <option value="CASH">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="CARD">Card</option>
                  <option value="BANK_TRANSFER">Bank Transfer</option>
                  <option value="CHEQUE">Cheque</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  value={searchForm.fromDate}
                  onChange={(e) => updateForm("fromDate", e.target.value)}
                  className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  value={searchForm.toDate}
                  onChange={(e) => updateForm("toDate", e.target.value)}
                  className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={handleReset}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-xs transition"
              >
                <i className="fas fa-redo" /> Reset
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"
                }`}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search" />
                    Search
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Results */}
      <section className="relative mt-2 rounded-xl overflow-hidden shadow-md border border-blue-200 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex-shrink-0">
          <h2 className="text-base font-bold text-blue-800">
            📋 Order Results
            {orders.length > 0 && (
              <span className="text-xs font-normal text-blue-600 ml-2">
                ({totalElements} order{totalElements !== 1 ? "s" : ""})
              </span>
            )}
          </h2>
        </div>
        <div className="overflow-x-auto flex-1">
          <table className="min-w-full text-sm text-gray-800 table-auto">
            <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase tracking-wide sticky top-0">
              <tr>
                <Th col="billNo" label="Invoice" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="createdOn" label="Date" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="customerName" label="Customer" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="finalAmount" label="Gross" className="text-right" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="returnAmount" label="Return" className="text-right" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="netSale" label="Net" className="text-right" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="paymentMode" label="Payment" className="text-center" sortConfig={sortConfig} onSort={handleSort} />
                {isAdmin && <Th col="netProfit" label="Profit" className="text-right" sortConfig={sortConfig} onSort={handleSort} />}
                <Th col="orderStatus" label="Status" className="text-center" sortConfig={sortConfig} onSort={handleSort} />
                <th className="py-2 px-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!hasSearched ? (
                <tr>
                  <td colSpan={isAdmin ? 10 : 9} className="py-10 text-center text-gray-400">
                    <i className="fas fa-search text-4xl mb-3 block" />
                    <p className="text-sm">Use the search above to find orders.</p>
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 10 : 9} className="py-10 text-center text-gray-400">
                    <i className="fas fa-inbox text-4xl mb-3 block" />
                    <p className="text-sm">No orders found. Try different search criteria.</p>
                  </td>
                </tr>
              ) : (
                sortedOrders.map((order, idx) => {
                  const status = order.orderStatus || "";
                  const statusCfg = {
                    CONFIRMED:      { dot: "🟢", label: "Confirmed",      cls: "bg-green-100 text-green-700" },
                    PENDING:        { dot: "🟡", label: "Pending",        cls: "bg-yellow-100 text-yellow-700" },
                    PARTIAL_RETURN: { dot: "🟠", label: "Partial Return", cls: "bg-orange-100 text-orange-700" },
                    RETURNED:       { dot: "🔴", label: "Returned",       cls: "bg-red-100 text-red-700" },
                    CANCELLED:      { dot: "⛔", label: "Cancelled",      cls: "bg-gray-200 text-gray-600" },
                    ORDER_FAILED:   { dot: "⛔", label: "Failed",         cls: "bg-gray-100 text-gray-600" },
                  }[status] || { dot: "⚪", label: status, cls: "bg-gray-100 text-gray-500" };

                  const returnAmt = order.returnAmount != null ? Number(order.returnAmount) : 0;
                  const hasReturn = returnAmt > 0;

                  const fmtDate = (d) => {
                    if (!d) return "—";
                    const dt = new Date(d);
                    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
                  };

                  return (
                    <tr key={order.orderId ?? idx}
                      className={`border-t hover:bg-blue-50 transition-colors ${
                        status === "RETURNED" ? "bg-red-50" :
                        status === "PARTIAL_RETURN" ? "bg-orange-50" :
                        idx % 2 === 0 ? "bg-white" : "bg-blue-50"
                      }`}>
                      <td className="py-2 px-3 font-semibold text-indigo-700 text-xs">{order.billNo || order.orderId || "—"}</td>
                      <td className="py-2 px-3 text-xs text-gray-500">{fmtDate(order.createdOn)}</td>
                      <td className="py-2 px-3 text-xs">
                        <div className="font-medium text-gray-800">{order.customerName || "—"}</div>
                        <div className="text-gray-400">{order.customerMobile || ""}</div>
                      </td>
                      <td className="py-2 px-3 text-right text-xs font-medium">
                        ₹{order.finalAmount != null ? Number(order.finalAmount).toFixed(2) : "—"}
                      </td>
                      <td className={`py-2 px-3 text-right text-xs font-medium ${hasReturn ? "text-red-600" : "text-gray-400"}`}>
                        {hasReturn ? `₹${returnAmt.toFixed(2)}` : "₹0"}
                      </td>
                      <td className="py-2 px-3 text-right text-xs font-bold text-blue-700">
                        ₹{order.netSale != null ? Number(order.netSale).toFixed(2) : "—"}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full">
                          {order.paymentMode || "—"}
                        </span>
                      </td>
                      {isAdmin && (
                        <td className="py-2 px-3 text-right text-xs font-semibold text-teal-700">
                          ₹{order.netProfit != null ? Number(order.netProfit).toFixed(2) : "—"}
                        </td>
                      )}
                      <td className="py-2 px-3 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${statusCfg.cls}`}>
                          {statusCfg.dot} {statusCfg.label}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <div className="relative flex items-center justify-center">
                          <button
                            onClick={() => setActionOpen(actionOpen === order.orderId ? null : order.orderId)}
                            className="w-6 h-6 bg-gray-50 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700 flex items-center justify-center transition-colors"
                          >
                            <i className="fas fa-ellipsis-v text-xs" />
                          </button>
                          {actionOpen === order.orderId && (
                            <div
                              className="absolute right-0 top-7 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[150px]"
                              onMouseLeave={() => setActionOpen(null)}
                            >
                              <button
                                onClick={async () => {
                                  setActionOpen(null);
                                  try { const url = await previewBillPDF(order.orderId); setPreviewUrl(url); }
                                  catch { setToast({ type: "error", message: "Failed to load preview" }); }
                                }}
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-blue-500 hover:bg-blue-50 transition-colors"
                              >
                                <i className="fas fa-eye text-sm" /> Preview Invoice
                              </button>
                              <button
                                onClick={() => { setActionOpen(null); setPrintTarget(order.orderId); }}
                                disabled={billLoading}
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-400 hover:bg-red-50 transition-colors disabled:opacity-40"
                              >
                                <i className="fas fa-print text-sm" /> Print Invoice
                              </button>
                              <button
                                onClick={async () => {
                                  setActionOpen(null);
                                  try { await downloadBillPDF(order.orderId); }
                                  catch { setToast({ type: "error", message: "Failed to download invoice" }); }
                                }}
                                disabled={billLoading}
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-green-500 hover:bg-green-50 transition-colors disabled:opacity-40"
                              >
                                <i className="fas fa-download text-sm" /> Download PDF
                              </button>
                              <button
                                onClick={() => { setActionOpen(null); setHistoryModal({ orderId: order.orderId, billNo: order.billNo }); }}
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-indigo-400 hover:bg-indigo-50 transition-colors"
                              >
                                <i className="fas fa-clipboard-list text-sm" /> Return History
                              </button>
                              {status !== "CANCELLED" && status !== "RETURNED" && (
                                <button
                                  onClick={() => { setActionOpen(null); setReturnModal(order); }}
                                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-orange-500 hover:bg-orange-50 transition-colors"
                                >
                                  <i className="fas fa-undo-alt text-sm" /> Process Return
                                </button>
                              )}
                              {isAdmin && status !== "CANCELLED" && (
                                <>
                                  <div className="border-t border-gray-100 my-1" />
                                  <button
                                    onClick={() => { setActionOpen(null); setCancelConfirm({ orderId: order.orderId, billNo: order.billNo }); }}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition-colors"
                                  >
                                    <i className="fas fa-ban text-sm" /> Cancel Order
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-2 border-t border-gray-100 bg-white flex-shrink-0">
            <span className="text-xs text-gray-500">
              Showing {page * PAGE_SIZE + 1}–
              {Math.min((page + 1) * PAGE_SIZE, totalElements)} of{" "}
              {totalElements}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(0)}
                disabled={page === 0}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                «
              </button>
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 0}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => handlePageChange(i)}
                  className={`px-2 py-1 text-xs rounded border ${i === page ? "bg-indigo-500 text-white border-indigo-500" : "border-gray-200 hover:bg-gray-50"}`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages - 1}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                ›
              </button>
              <button
                onClick={() => handlePageChange(totalPages - 1)}
                disabled={page >= totalPages - 1}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                »
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Print Format Picker Modal */}
      {printTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-80 overflow-hidden">
            <div className="px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
              <span className="text-sm font-bold text-blue-800">🖨 Select Print Format</span>
            </div>
            <div className="p-5 flex flex-col gap-3">
              <button
                onClick={async () => {
                  const id = printTarget; setPrintTarget(null);
                  try { await printBill(id, "pos"); }
                  catch { setToast({ type: "error", message: "Failed to print" }); }
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-blue-200 hover:border-blue-400 hover:bg-blue-50 transition-colors text-left"
              >
                <i className="fas fa-receipt text-blue-500 text-xl w-6" />
                <div>
                  <div className="font-semibold text-gray-800 text-sm">POS / Thermal</div>
                  <div className="text-xs text-gray-400">80mm receipt printer</div>
                </div>
              </button>
              <button
                onClick={async () => {
                  const id = printTarget; setPrintTarget(null);
                  try { await printBill(id, "a4"); }
                  catch { setToast({ type: "error", message: "Failed to print" }); }
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50 transition-colors text-left"
              >
                <i className="fas fa-file-alt text-indigo-500 text-xl w-6" />
                <div>
                  <div className="font-semibold text-gray-800 text-sm">A4 Invoice</div>
                  <div className="text-xs text-gray-400">Standard A4 paper</div>
                </div>
              </button>
              <button
                onClick={() => setPrintTarget(null)}
                className="text-xs text-gray-400 hover:text-gray-600 text-center mt-1"
              >Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-xl shadow-2xl flex flex-col w-[90vw] h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex-shrink-0">
              <span className="text-sm font-bold text-blue-800">📄 Invoice Preview</span>
              <button
                onClick={() => { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}
                className="text-gray-500 hover:text-red-500 text-lg font-bold transition-colors"
              >×</button>
            </div>
            <iframe src={previewUrl} className="flex-1 w-full" title="Invoice Preview" />
          </div>
        </div>
      )}

      {/* Cancel Confirm Modal */}
      {cancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-80 overflow-hidden">
            <div className="px-5 py-3 bg-gradient-to-r from-red-100 via-orange-100 to-yellow-100 border-b border-red-200">
              <span className="text-sm font-bold text-red-800">⚠️ Cancel Order</span>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-700 mb-4">
                Cancel order <span className="font-semibold">{cancelConfirm.billNo}</span>? This will remove its GST records and cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setCancelConfirm(null)}
                  className="px-4 py-1.5 border border-gray-300 rounded-full text-gray-600 hover:bg-gray-50 text-xs"
                >No, Keep</button>
                <button
                  onClick={async () => {
                    const { orderId } = cancelConfirm;
                    setCancelConfirm(null);
                    try {
                      await orderService.cancelOrder(orderId);
                      setToast({ type: "success", message: "Order cancelled successfully" });
                      fetchOrders(page);
                    } catch (err) {
                      setToast({ type: "error", message: err.response?.data?.message || "Failed to cancel order" });
                    }
                  }}
                  className="px-4 py-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 text-xs font-semibold"
                >Yes, Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Return Modal */}
      {returnModal && (
        <ReturnModal
          order={returnModal}
          onClose={() => setReturnModal(null)}
          onSuccess={(res) => {
            setReturnModal(null);
            setToast({ type: "success", message: `Return processed. Refund: ₹${Number(res.refundAmount).toFixed(2)}` });
            fetchOrders(page);
          }}
        />
      )}

      {/* Return History Modal */}
      {historyModal && (
        <ReturnHistoryModal
          orderId={historyModal.orderId}
          billNo={historyModal.billNo}
          onClose={() => setHistoryModal(null)}
        />
      )}
    </div>
  );
};

export default OrderSearch;
